import TableOnly from "components/DataTable/TableOnly";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import GetNylasAuth from "pages/settings/EmailClient/EmailAuth/GetNylasAuth";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import SaveGrant from "./EmailAuth/SaveGrant";
import ImportLabels from "./ImportLabels/ImportLabels";

export default function EmailClient() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const REAUTH_FLAG_KEY = "emailClientReauth";
  const REAUTH_EMAIL_KEY = "emailClientReauthEmail";
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [emailGrants, setEmailGrants] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  // Add Label
  const [showAddLabelModal, setShowAddLabelModal] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);

  const [loading, setLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const resetReauthMarkers = () => {
    sessionStorage.removeItem(REAUTH_FLAG_KEY);
    sessionStorage.removeItem(REAUTH_EMAIL_KEY);
  };

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /email-grants?page=&limit=)
  const getEmailGrant = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/email-grants?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setEmailGrants(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, searchTerm, pageLimit, filterBy.value]
  );

  // Delete Email
  const deleteEmailGrant = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/email-grant/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        await getEmailGrant();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          })
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        })
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // set default email grant
  const setDefaultEmailGrant = async (id) => {
    try {
      const { data } = await API.post(
        "/email-grants/set-default",
        { id: id },
        { headers: authHeader() }
      );
      if (data.success) {
        setEmailGrants((prev) => data.data);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to set default",
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    try {
      const { data } = await API.post(
        "/email-grant-sort-up",
        { emailGrantId: id },
        { headers: authHeader() }
      );
      if (data.success) {
        await getEmailGrant();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (id) => {
    try {
      const { data } = await API.post(
        "/email-grant-sort-down",
        { emailGrantId: id },
        { headers: authHeader() }
      );
      if (data.success) {
        await getEmailGrant();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleInitialLabelImport = (grantEmail) => {
    setSelectedLabel({ email: grantEmail });
    setShowAddLabelModal(true);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getEmailGrant(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber }
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy]);

  /*   Table functions
   ********************************************* */
  let filters = [
    { value: "name", name: "Data Name" },
    { value: "contactName", name: "Contact Name" },
    { value: "contactNumber", name: "Contact Number" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "ID", value: "emailGrantId" },
    { name: "Email", value: "email" },
    { name: "Grant", value: "grantId" },
    { name: "Default?", value: "isDefault" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item.emailGrantId}</td>
      <td className="align-middle py-1">{item.email}</td>
      <td className="align-middle py-1">{truncateString(item.grantId, 10)}</td>
      <td className="align-middle py-1">{item.isDefault ? "Yes" : "No"}</td>
      <td className="align-middle py-1 text-end table-action">
        {!item?.isDefault && (
          <button
            onClick={() => {
              setDefaultEmailGrant(item.id);
            }}
            disabled={item.isDefault || loading}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-check-bold p-0"></i>
          </button>
        )}
        <button
          onClick={(e) => {
            sessionStorage.setItem(REAUTH_FLAG_KEY, "true");
            sessionStorage.setItem(REAUTH_EMAIL_KEY, item.email);
            setShowAddModal(true);
          }}
          className="btn btn-success btn-sm py-0 px-1 me-1"
        >
          Re-Auth
        </button>
        <button
          onClick={(e) => {
            setSelectedLabel(item);
            setShowAddLabelModal(true);
          }}
          className="btn btn-primary btn-sm py-0"
        >
          T
        </button>
        <button
          onClick={() => {
            sortUp(item.emailGrantId);
          }}
          disabled={
            emailGrants.findIndex((grant) => grant.id === item.id) === 0
          } // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-up p-0"></i>
        </button>
        <button
          onClick={() => {
            sortDown(item.emailGrantId);
          }}
          disabled={
            emailGrants.findIndex((grant) => grant.id === item.id) ===
            emailGrants.length - 1
          } // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-down"></i>
        </button>
        {/* <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button> */}

        <DeleteModalButton
          btnClass={"btn btn-sm  px-1"}
          deleteFunc={deleteEmailGrant}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  useEffect(() => {
    let timeout = setTimeout(() => {
      let checkEmailGrant = searchParams.get("email_clients");
      if (checkEmailGrant === "true") {
        setShowAddModal(true);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="">
      <h2 className="mb-3 ps-2">Email Clients</h2>
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {/* {buttons()} */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getEmailGrant({ query: searchTerm, page: 1 });
              } else {
                getEmailGrant({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getEmailGrant({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          <div className="">
            <button
              onClick={() => {
                resetReauthMarkers();
                setShowAddModal(true);
              }}
              type="button"
              className="btn btn-primary text-nowrap ms-1"
            >
              Add Email
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={emailGrants}
            disableSorting={true}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/*   Label Add Modal
       ********************************************* */}
      <Modal
        show={showAddLabelModal}
        onHide={() => setShowAddLabelModal(false)}
        contentClassName="bg-transparent"
        size="lg"
      >
        <Modal.Body
          className="shadow-lg bg-white px-3 custom-border-radius"
          // style={{ borderRadius: 15 }}
        >
          <ImportLabels
            selected={selectedLabel}
            showModal={setShowAddLabelModal}
          />
        </Modal.Body>
      </Modal>

      {/*   Add Modal
       ********************************************* */}
      <Modal
        show={showAddModal}
        onHide={() => {
          resetReauthMarkers();
          setShowAddModal(false);
        }}
        contentClassName="bg-transparent"
      >
        <Modal.Body
          className="shadow-lg bg-white px-3 custom-border-radius-sm"
          style={{ borderRadius: 15 }}
        >
          {searchParams.get("email_clients") === "true" ? (
            <SaveGrant
              showAddModal={showAddModal}
              setShowAddModal={setShowAddModal}
              refreshFunc={getEmailGrant}
              handleInitialLabelImport={handleInitialLabelImport}
            />
          ) : (
            <GetNylasAuth showAddModal={showAddModal} />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
